import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatValue } from "@/lib/api";
import { Loader2, TrendingUp, TrendingDown, ArrowLeftRight, ArrowRight } from "lucide-react";
import { ShareButtons } from "@/components/ShareButtons";
import DmeTag from "@/components/DmeTag";

function OppCard({ o, kind }) {
  const buy = kind === "buy";
  const color = buy ? "var(--accent)" : "var(--negative)";
  return (
    <div className="card-bordered p-4" style={{ borderLeft: `2px solid ${color}` }} data-testid={`arb-${kind}-${o.league_id}-${o.roster_id}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="font-semibold text-sm truncate flex items-center gap-2">
          {o.manager}
          {o.is_me && <span className="text-[8px] font-mono px-1.5 py-0.5 rounded-sm" style={{ background: "var(--accent)", color: "#000" }} data-testid="arb-you-badge">YOU</span>}
        </div>
        <div className="font-mono text-sm" style={{ color }}>{o.value_delta_pct > 0 ? "+" : ""}{o.value_delta_pct}%</div>
      </div>
      <Link to={`/app/leagues/${o.league_id}/context`} className="text-[11px] text-[var(--text-3)] hover:text-[var(--accent)] font-mono">{o.league_name}</Link>
      {o.held_by && o.is_traded && (
        <div className="text-[10px] text-[var(--text-3)] mt-0.5" data-testid={`arb-held-${o.league_id}-${o.roster_id}`}>
          Currently held by <span className="text-[var(--text-2)]">{o.held_by}</span> <span className="text-[var(--text-3)]">(traded pick)</span>
        </div>
      )}
      <div className="flex items-end justify-between mt-3">
        <div>
          <div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">PROJECTED 1ST</div>
          <div className="font-mono text-lg" style={{ color }}>{o.pick_label}</div>
        </div>
        <div className="text-right">
          <div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono flex items-center gap-1 justify-end">SIM vs MARKET <DmeTag /></div>
          <div className="font-mono text-sm">{formatValue(o.pick_value)} <span className="text-[var(--text-3)]">/ {formatValue(o.market_value)}</span></div>
        </div>
      </div>
      <p className="text-[11px] text-[var(--text-2)] mt-2 italic">
        {buy
          ? `Acquire ${o.manager}'s 1st${o.is_traded ? ` from ${o.held_by}` : ""} — the sim projects ${o.pick_label} but the market prices a flat 1st.`
          : `Your 1st here (${o.manager}'s original pick) projects to land late (${o.pick_label}) — sell it now while the market still pays a flat-1st price.`}
      </p>
      {buy && <div className="mt-2"><ShareButtons testid={`share-arb-${o.league_id}-${o.roster_id}`} text={`🟢 BUY signal: ${o.manager}'s 1st projects ${o.pick_label} (+${o.value_delta_pct}% over market). Pick arbitrage spotted with Dynasty Metrics.`} title={`Pick arbitrage: buy a projected ${o.pick_label} at a +${o.value_delta_pct}% discount`} /></div>}
    </div>
  );
}

export default function PickArbitrage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/portfolio/pick-arbitrage").then(r => setData(r.data)).catch(() => setData({ error: true })); }, []);

  if (data === null) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="card-bordered p-12 flex flex-col items-center gap-3" data-testid="arb-loading">
          <Loader2 className="w-6 h-6 spin text-[var(--accent)]" />
          <div className="text-sm text-[var(--text-3)]">Scanning every synced league for mispriced draft capital…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="pick-arbitrage">
      <div className="flex items-center gap-3 mb-1">
        <ArrowLeftRight className="w-6 h-6 text-[var(--accent)]" />
        <h1 className="font-display text-3xl font-black uppercase tracking-tight">Pick Arbitrage</h1>
      </div>
      <p className="text-sm text-[var(--text-2)] mb-6 max-w-2xl">
        Every market prices a "1st" the same. Our simulator prices it by where it actually lands. We scanned{" "}
        <span className="text-[var(--accent)] font-mono">{data.leagues_scanned}/{data.total_synced}</span> of your leagues for the gap.
      </p>

      {(!data.buys?.length && !data.sells?.length) ? (
        <div className="card-bordered p-8 text-center text-sm text-[var(--text-3)]" data-testid="arb-empty">
          No clear arbitrage right now — sync more leagues (and connect a Sleeper account) to surface mispriced picks.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center gap-2 text-xs tracking-widest font-mono mb-3" style={{ color: "var(--accent)" }}>
              <TrendingUp className="w-4 h-4" /> BUY — UNDERVALUED PICKS TO ACQUIRE
            </div>
            <div className="space-y-3">
              {data.buys.map((o, i) => <OppCard key={i} o={o} kind="buy" />)}
              {!data.buys.length && <div className="text-sm text-[var(--text-3)]">None found.</div>}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 text-xs tracking-widest font-mono mb-3" style={{ color: "var(--negative)" }}>
              <TrendingDown className="w-4 h-4" /> SELL — OVERVALUED PICKS TO MOVE
            </div>
            <div className="space-y-3">
              {data.sells.map((o, i) => <OppCard key={i} o={o} kind="sell" />)}
              {!data.sells.length && <div className="text-sm text-[var(--text-3)]">None found.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
